<script lang="ts">
  import { authState, refreshCustomClaims } from '../../lib/auth.svelte';
  import {
    getUserSubscriptions,
    createCheckoutSession,
    createPortalSession,
    subscribeToAllUserSubscriptions,
    subscribeToCustomerPayments,
    extractPaymentMethod,
    PRICE_IDS,
    PLAN_PRICE_IDS,
    type PaymentMethod,
    type Invoice,
  } from '../../lib/stripe';
  import { doc, getDoc, updateDoc } from 'firebase/firestore';
  import { db } from '../../lib/firebase';
  import { onMount } from 'svelte';

  let subscriptions = $state<any[]>([]);
  let payments = $state<Invoice[]>([]);
  let loading = $state(true);
  let checkoutLoading = $state<string | null>(null);
  let portalLoading = $state(false);
  let updateCardLoading = $state(false);
  let switchingTier = $state<string | null>(null);
  let error = $state('');
  let successMessage = $state('');
  let billingCycle = $state<'monthly' | 'annual'>('monthly');

  // User document data for trial users
  let userDocData = $state<{selectedPlan?: string; subscriptionStatus?: string} | null>(null);

  // Check URL params for checkout status
  function checkUrlParams() {
    const hash = window.location.hash;
    if (hash.includes('success=true')) {
      successMessage = 'Payment successful! Your subscription is now active.';
      // Clean up URL
      window.history.replaceState(null, '', window.location.pathname + '#dashboard?tab=subscription');
    } else if (hash.includes('canceled=true')) {
      error = 'Checkout was canceled. You can try again when ready.';
      window.history.replaceState(null, '', window.location.pathname + '#dashboard?tab=subscription');
    }
  }

  // Current subscription info (includes problem statuses like past_due)
  let currentPlan = $derived(() => {
    if (subscriptions.length === 0) return null;
    // Prefer active/trialing, but also show past_due/unpaid
    const activeSub = subscriptions.find(s =>
      ['active', 'trialing', 'past_due', 'unpaid'].includes(s.status)
    ) || subscriptions[0];
    const productName = activeSub?.items?.[0]?.price?.product?.name || '';
    const priceAmount = activeSub?.items?.[0]?.price?.unit_amount;
    const priceInterval = activeSub?.items?.[0]?.price?.recurring?.interval;

    return {
      name: productName,
      status: activeSub?.status,
      currentPeriodEnd: activeSub?.current_period_end?.seconds
        ? new Date(activeSub.current_period_end.seconds * 1000)
        : null,
      cancelAtPeriodEnd: activeSub?.cancel_at_period_end,
      stripeLink: activeSub?.stripeLink,
      priceAmount: priceAmount ? priceAmount / 100 : null,
      priceInterval: priceInterval || 'month',
      rawSubscription: activeSub,
    };
  });

  // Extract payment method from subscription
  let paymentMethod = $derived(() => {
    const plan = currentPlan();
    if (!plan?.rawSubscription) return null;
    return extractPaymentMethod(plan.rawSubscription);
  });

  // Check if subscription has payment issues
  let hasPaymentIssue = $derived(() => {
    const plan = currentPlan();
    return plan?.status === 'past_due' || plan?.status === 'unpaid';
  });

  // Check if user is in trial mode (no Stripe subscription, has trial status in user doc)
  let isTrialUser = $derived(() => {
    return subscriptions.length === 0 && userDocData?.subscriptionStatus === 'trial';
  });

  // Get trial plan from user document (for trial users without Stripe subscription)
  let trialPlan = $derived(() => {
    if (!isTrialUser()) return null;
    return userDocData?.selectedPlan || null;
  });

  onMount(() => {
    // Check for success/cancel from Stripe redirect
    checkUrlParams();

    if (authState.user) {
      // Real-time subscription updates (all statuses)
      const unsubscribeSubs = subscribeToAllUserSubscriptions(
        authState.user.uid,
        (subs) => {
          subscriptions = subs;
          loading = false;
        }
      );

      // Real-time payment/invoice updates
      const unsubscribePayments = subscribeToCustomerPayments(
        authState.user.uid,
        (p) => {
          payments = p;
        }
      );

      // Initial fetch
      getUserSubscriptions(authState.user.uid)
        .then((subs) => {
          subscriptions = subs;
        })
        .catch((err: any) => {
          error = err.message;
        })
        .finally(() => {
          loading = false;
        });

      // Fetch user document for trial info
      getDoc(doc(db, 'users', authState.user.uid))
        .then((userDoc) => {
          if (userDoc.exists()) {
            const data = userDoc.data();
            userDocData = {
              selectedPlan: data.selectedPlan,
              subscriptionStatus: data.subscriptionStatus,
            };
          }
        })
        .catch((err) => {
          console.warn('Could not fetch user document:', err);
        });

      return () => {
        unsubscribeSubs();
        unsubscribePayments();
      };
    } else {
      loading = false;
    }
  });

  const plans = [
    {
      id: 'core',
      name: 'Core',
      monthlyPrice: 92,
      annualPrice: 83,
      description: 'The essentials for smaller teams that need live order visibility.',
      features: [
        '50 orders per month',
        'Orders, Inventory, Contacts modules',
        'Unlimited users & business units',
        'Role-based permissions',
      ],
      priceId: {
        monthly: PLAN_PRICE_IDS.core.monthly || PRICE_IDS.basic,
        annual: PLAN_PRICE_IDS.core.annual,
      },
    },
    {
      id: 'more',
      name: 'More',
      monthlyPrice: 195,
      annualPrice: 176,
      description: 'Adds warehouse workflows plus room to bolt on advanced modules.',
      features: [
        '500 orders per month',
        'Everything in Core',
        'Warehouse management included',
        'Manufacture & Freight as add-ons',
        'Best for multi-site management',
      ],
      priceId: {
        monthly: PLAN_PRICE_IDS.more.monthly || PRICE_IDS.pro,
        annual: PLAN_PRICE_IDS.more.annual,
      },
      highlighted: true,
    },
    {
      id: 'complete',
      name: 'Complete',
      monthlyPrice: 320,
      annualPrice: 288,
      description: 'Full-stack suite for high-volume teams that need every module.',
      features: [
        '1,000 orders per month',
        'All modules included',
        'Manufacture module included',
        'Freight module included',
        'Priority support',
      ],
      priceId: {
        monthly: PLAN_PRICE_IDS.complete.monthly || PRICE_IDS.enterprise,
        annual: PLAN_PRICE_IDS.complete.annual,
      },
    },
  ];

  async function handleSubscribe(plan: typeof plans[0]) {
    if (!authState.user) return;

    const priceId = billingCycle === 'annual' && plan.priceId.annual
      ? plan.priceId.annual
      : plan.priceId.monthly;

    checkoutLoading = plan.id;
    error = '';

    try {
      const checkoutUrl = await createCheckoutSession(
        authState.user.uid,
        priceId,
        `${window.location.origin}/#dashboard?tab=subscription&success=true`,
        `${window.location.origin}/#dashboard?tab=subscription&canceled=true`
      );
      window.location.href = checkoutUrl;
    } catch (err: any) {
      error = err.message;
      checkoutLoading = null;
    }
  }

  // Switch trial tier (for users in trial mode without Stripe subscription)
  async function handleSwitchTrialTier(newPlanId: string) {
    if (!authState.user || !isTrialUser()) return;

    switchingTier = newPlanId;
    error = '';

    try {
      // Update user document with new plan and trigger claims refresh
      const userRef = doc(db, 'users', authState.user.uid);
      await updateDoc(userRef, {
        selectedPlan: newPlanId,
        claimsSet: false, // Trigger claims handler to update sublevel
      });

      // Wait briefly for claims handler to process
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Refresh custom claims to get updated sublevel
      await refreshCustomClaims();

      // Update local state
      userDocData = { ...userDocData, selectedPlan: newPlanId };

      const planName = newPlanId.charAt(0).toUpperCase() + newPlanId.slice(1);
      successMessage = `Switched to ${planName} trial! Your module access has been updated.`;
    } catch (err: any) {
      error = `Failed to switch tier: ${err.message}`;
    } finally {
      switchingTier = null;
    }
  }

  async function handleManageSubscription() {
    if (!authState.user) return;

    // Try using stripeLink from subscription first
    const plan = currentPlan();
    if (plan?.stripeLink) {
      window.open(plan.stripeLink, '_blank');
      return;
    }

    // Otherwise create a portal session
    portalLoading = true;
    error = '';

    try {
      const portalUrl = await createPortalSession(
        authState.user.uid,
        `${window.location.origin}/#dashboard?tab=subscription`
      );
      window.location.href = portalUrl;
    } catch (err: any) {
      error = err.message;
      portalLoading = false;
    }
  }

  async function handleUpdateCard() {
    if (!authState.user) return;

    updateCardLoading = true;
    error = '';

    try {
      const portalUrl = await createPortalSession(
        authState.user.uid,
        `${window.location.origin}/#dashboard?tab=subscription`
      );
      // Portal opens to payment method section by default
      window.location.href = portalUrl;
    } catch (err: any) {
      error = err.message;
      updateCardLoading = false;
    }
  }

  function dismissSuccess() {
    successMessage = '';
  }

  function formatCurrency(amount: number, currency = 'aud') {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount);
  }

  function getCardBrandIcon(brand: string) {
    // Return brand name for display
    const brands: Record<string, string> = {
      visa: 'Visa',
      mastercard: 'Mastercard',
      amex: 'Amex',
      discover: 'Discover',
      diners: 'Diners',
      jcb: 'JCB',
      unionpay: 'UnionPay',
    };
    return brands[brand?.toLowerCase()] || 'Card';
  }

  function getStatusBadgeClass(status: string) {
    switch (status) {
      case 'active':
      case 'trialing':
      case 'succeeded':
        return 'status-success';
      case 'past_due':
      case 'unpaid':
      case 'failed':
        return 'status-warning';
      case 'canceled':
        return 'status-error';
      default:
        return 'status-neutral';
    }
  }

  function formatDate(date: Date | null) {
    if (!date) return '';
    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }

  function getAnnualSavings(monthly: number, annual: number) {
    const yearlyIfMonthly = monthly * 12;
    const yearlyIfAnnual = annual * 12;
    return yearlyIfMonthly - yearlyIfAnnual;
  }
</script>

<div class="subscription-page">
  {#if successMessage}
    <div class="success-banner" role="status">
      <svg viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
      </svg>
      {successMessage}
      <button class="success-dismiss" onclick={dismissSuccess}>Dismiss</button>
    </div>
  {/if}

  {#if error}
    <div class="error-banner" role="alert">
      {error}
      <button class="error-dismiss" onclick={() => error = ''}>Dismiss</button>
    </div>
  {/if}

  <!-- Current Subscription Section -->
  <section class="current-subscription">
    <h2>Current Subscription</h2>

    {#if loading}
      <div class="loading-card">
        <div class="spinner"></div>
        <p>Loading subscription...</p>
      </div>
    {:else if currentPlan() && currentPlan()!.name}
      <div class="subscription-card" class:active={!hasPaymentIssue()} class:warning={hasPaymentIssue()}>
        <div class="subscription-header">
          <div class="plan-badge" class:badge-warning={hasPaymentIssue()}>
            <span class="status-dot" class:dot-warning={hasPaymentIssue()}></span>
            {#if currentPlan()!.status === 'trialing'}
              Trial
            {:else if currentPlan()!.status === 'past_due'}
              Payment Due
            {:else if currentPlan()!.status === 'unpaid'}
              Unpaid
            {:else}
              Active
            {/if}
          </div>
          <h3>{currentPlan()!.name}</h3>
          {#if currentPlan()!.priceAmount}
            <span class="price-tag">
              {formatCurrency(currentPlan()!.priceAmount!)}/{currentPlan()!.priceInterval}
            </span>
          {/if}
        </div>

        {#if hasPaymentIssue()}
          <div class="payment-warning">
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
            <span>Your payment method needs attention. Please update your card to continue service.</span>
            <button class="btn-update-card-inline" onclick={handleUpdateCard} disabled={updateCardLoading}>
              {updateCardLoading ? 'Loading...' : 'Update Card'}
            </button>
          </div>
        {/if}

        <div class="subscription-details">
          {#if currentPlan()!.cancelAtPeriodEnd}
            <p class="cancel-notice">
              Cancels on {formatDate(currentPlan()!.currentPeriodEnd)}
            </p>
          {:else if currentPlan()!.currentPeriodEnd}
            <p>Renews on {formatDate(currentPlan()!.currentPeriodEnd)}</p>
          {/if}
        </div>
        <button
          class="btn-manage"
          onclick={handleManageSubscription}
          disabled={portalLoading}
        >
          {portalLoading ? 'Loading...' : 'Manage Subscription'}
        </button>
      </div>
    {:else if isTrialUser()}
      <!-- Trial user subscription card -->
      {@const trialPlanName = trialPlan() ? trialPlan()!.charAt(0).toUpperCase() + trialPlan()!.slice(1) : 'Unknown'}
      {@const trialPlanInfo = plans.find(p => p.id === trialPlan())}
      <div class="subscription-card trial">
        <div class="subscription-header">
          <div class="plan-badge badge-trial">
            <span class="status-dot dot-trial"></span>
            Trial
          </div>
          <h3>{trialPlanName}</h3>
          {#if trialPlanInfo}
            <span class="price-tag">
              {formatCurrency(billingCycle === 'annual' ? trialPlanInfo.annualPrice : trialPlanInfo.monthlyPrice)}/month after trial
            </span>
          {/if}
        </div>

        <div class="trial-info">
          <svg viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
          </svg>
          <span>You're on a 14-day free trial. Add payment details to continue after your trial ends, or switch plans below.</span>
        </div>

        <div class="subscription-details">
          <p>Trial plan: <strong>{trialPlanName}</strong></p>
        </div>
      </div>
    {:else}
      <div class="subscription-card empty">
        <div class="empty-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="4" width="18" height="16" rx="2" />
            <path d="M3 10h18" />
          </svg>
        </div>
        <h3>No Active Subscription</h3>
        <p>Choose a plan below to get started with EQUOS</p>
      </div>
    {/if}
  </section>

  <!-- Billing & Payment Section -->
  {#if currentPlan() && currentPlan()!.name}
    <section class="billing-section">
      <h2>Billing & Payment</h2>
      <div class="billing-grid">
        <!-- Payment Method Card -->
        <div class="billing-card">
          <div class="billing-card-header">
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
              <path fill-rule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clip-rule="evenodd" />
            </svg>
            <h4>Payment Method</h4>
          </div>
          {#if paymentMethod()}
            <div class="payment-method-info">
              <div class="card-details">
                <span class="card-brand">{getCardBrandIcon(paymentMethod()!.brand)}</span>
                <span class="card-number">•••• {paymentMethod()!.last4}</span>
              </div>
              <p class="card-expiry">Expires {paymentMethod()!.expMonth}/{paymentMethod()!.expYear}</p>
            </div>
          {:else}
            <p class="no-payment-method">No payment method on file</p>
          {/if}
          <button
            class="btn-update-card"
            onclick={handleUpdateCard}
            disabled={updateCardLoading}
          >
            {updateCardLoading ? 'Loading...' : 'Update Card'}
          </button>
        </div>

        <!-- Next Invoice Card -->
        <div class="billing-card">
          <div class="billing-card-header">
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clip-rule="evenodd" />
            </svg>
            <h4>Next Invoice</h4>
          </div>
          {#if currentPlan()!.priceAmount && currentPlan()!.currentPeriodEnd}
            <div class="next-invoice-info">
              <p class="invoice-amount">{formatCurrency(currentPlan()!.priceAmount!)}</p>
              <p class="invoice-date">Due {formatDate(currentPlan()!.currentPeriodEnd)}</p>
            </div>
          {:else}
            <p class="no-invoice">No upcoming invoice</p>
          {/if}
        </div>
      </div>
    </section>

    <!-- Invoice History Section -->
    {#if payments.length > 0}
      <section class="invoices-section">
        <h2>Recent Invoices</h2>
        <div class="invoices-list">
          {#each payments as payment}
            <div class="invoice-row">
              <div class="invoice-date-col">
                {formatDate(payment.created)}
              </div>
              <div class="invoice-amount-col">
                {formatCurrency(payment.amount / 100, payment.currency)}
              </div>
              <div class="invoice-status-col">
                <span class="status-badge {getStatusBadgeClass(payment.status)}">
                  {payment.status === 'succeeded' ? 'Paid' : payment.status}
                </span>
              </div>
              <div class="invoice-actions-col">
                {#if payment.invoiceUrl}
                  <a href={payment.invoiceUrl} target="_blank" rel="noopener noreferrer" class="invoice-link">
                    View Receipt
                  </a>
                {/if}
              </div>
            </div>
          {/each}
        </div>
        <button class="btn-view-all" onclick={handleManageSubscription}>
          View All in Stripe Portal
        </button>
      </section>
    {/if}
  {/if}

  <!-- Available Plans Section -->
  <section class="plans-section">
    <div class="plans-header">
      <h2>Available Plans</h2>
      <div class="billing-toggle">
        <button
          class:active={billingCycle === 'monthly'}
          onclick={() => billingCycle = 'monthly'}
        >
          Monthly
        </button>
        <button
          class:active={billingCycle === 'annual'}
          onclick={() => billingCycle = 'annual'}
        >
          Annual
          <span class="save-badge">Save 10%</span>
        </button>
      </div>
    </div>

    <div class="plans-grid">
      {#each plans as plan}
        {@const price = billingCycle === 'annual' ? plan.annualPrice : plan.monthlyPrice}
        {@const isCurrentPlan = currentPlan()?.name?.toLowerCase().includes(plan.name.toLowerCase())}
        <div class="plan-card" class:highlighted={plan.highlighted} class:current={isCurrentPlan}>
          {#if plan.highlighted}
            <div class="popular-badge">Most Popular</div>
          {/if}
          {#if isCurrentPlan}
            <div class="current-badge">Current Plan</div>
          {/if}

          <h3>{plan.name}</h3>
          <p class="plan-description">{plan.description}</p>

          <div class="plan-price">
            <span class="currency">$</span>
            <span class="amount">{price}</span>
            <span class="period">/month</span>
          </div>

          {#if billingCycle === 'annual'}
            <p class="annual-savings">
              Save ${getAnnualSavings(plan.monthlyPrice, plan.annualPrice)}/year
            </p>
          {/if}

          <ul class="features-list">
            {#each plan.features as feature}
              <li>
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
                {feature}
              </li>
            {/each}
          </ul>

          {#if authState.isAccountAdmin}
            {#if isTrialUser()}
              <!-- Trial user buttons -->
              {@const isCurrentTrialPlan = trialPlan() === plan.id}
              {#if isCurrentTrialPlan}
                <button class="btn-current-trial" disabled>Current Trial Plan</button>
              {:else}
                <button
                  class="btn-switch-tier"
                  onclick={() => handleSwitchTrialTier(plan.id)}
                  disabled={switchingTier !== null}
                >
                  {#if switchingTier === plan.id}
                    <span class="btn-spinner"></span>
                    Switching...
                  {:else}
                    Try This Plan
                  {/if}
                </button>
              {/if}
              <button
                class="btn-add-payment"
                class:btn-highlighted={plan.highlighted || isCurrentTrialPlan}
                onclick={() => handleSubscribe(plan)}
                disabled={checkoutLoading !== null || switchingTier !== null}
              >
                {#if checkoutLoading === plan.id}
                  <span class="btn-spinner"></span>
                  Processing...
                {:else}
                  Add Payment Details
                {/if}
              </button>
            {:else if isCurrentPlan}
              <button class="btn-current" disabled>Current Plan</button>
            {:else}
              <button
                class="btn-subscribe"
                class:btn-highlighted={plan.highlighted}
                onclick={() => handleSubscribe(plan)}
                disabled={checkoutLoading !== null}
              >
                {#if checkoutLoading === plan.id}
                  <span class="btn-spinner"></span>
                  Processing...
                {:else if currentPlan()?.name}
                  {plans.findIndex(p => p.name === plan.name) > plans.findIndex(p => currentPlan()?.name?.toLowerCase().includes(p.name.toLowerCase())) ? 'Upgrade' : 'Downgrade'}
                {:else}
                  Subscribe
                {/if}
              </button>
            {/if}
          {:else}
            <p class="contact-admin">Contact your account admin to change plans</p>
          {/if}
        </div>
      {/each}
    </div>
  </section>

  <!-- Help Section -->
  <section class="help-section">
    <h3>Need help choosing?</h3>
    <p>Contact our team for a personalized recommendation based on your business needs.</p>
    <a href="mailto:support@equosnine.com" class="help-link">
      <svg viewBox="0 0 20 20" fill="currentColor">
        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
      </svg>
      support@equosnine.com
    </a>
  </section>
</div>

<style>
  .subscription-page {
    max-width: 1200px;
    margin: 0 auto;
  }

  /* Error Banner - uses shared base styles */
  .error-banner {
    background: var(--color-error-bg);
    border: 1px solid #fecaca;
    color: var(--color-error);
    padding: 1rem 1.5rem;
    border-radius: var(--radius-md);
    margin-bottom: 2rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .error-dismiss {
    background: none;
    border: none;
    color: var(--color-error);
    cursor: pointer;
    font-weight: 500;
  }

  /* Section Styles */
  section {
    margin-bottom: 3rem;
  }

  h2 {
    font-size: 1.25rem;
    color: var(--color-gray-900);
    margin: 0 0 1.5rem;
  }

  /* Current Subscription */
  .loading-card {
    background: white;
    border-radius: var(--radius-lg);
    padding: 3rem;
    text-align: center;
    box-shadow: var(--shadow-sm);
  }

  /* Spinner uses shared .spinner animation */
  .loading-card .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid var(--color-gray-200);
    border-top-color: var(--color-primary);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin: 0 auto 1rem;
  }

  .subscription-card {
    background: white;
    border-radius: var(--radius-lg);
    padding: 1.5rem;
    box-shadow: var(--shadow-sm);
  }

  .subscription-card.active {
    border: 2px solid var(--color-success);
  }

  .subscription-card.empty {
    text-align: center;
    padding: 3rem;
  }

  .subscription-card.trial {
    border: 2px solid var(--color-info);
  }

  .plan-badge.badge-trial {
    background: var(--color-info-bg);
    color: var(--color-info);
  }

  .status-dot.dot-trial {
    background: var(--color-info);
  }

  .trial-info {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    background: var(--color-info-bg);
    border: 1px solid #93c5fd;
    border-radius: var(--radius-md);
    padding: 0.75rem 1rem;
    margin: 1rem 0;
    color: var(--color-info);
    font-size: 0.85rem;
    line-height: 1.5;
  }

  .trial-info svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    margin-top: 2px;
  }

  .empty-icon {
    width: 64px;
    height: 64px;
    margin: 0 auto 1rem;
    color: var(--color-gray-400);
  }

  .empty-icon svg {
    width: 100%;
    height: 100%;
  }

  .subscription-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .plan-badge {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: var(--color-success-bg);
    color: var(--color-success);
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
  }

  .status-dot {
    width: 8px;
    height: 8px;
    background: var(--color-success);
    border-radius: 50%;
  }

  .subscription-header h3 {
    margin: 0;
    font-size: 1.5rem;
    color: var(--color-gray-900);
  }

  .subscription-details p {
    margin: 0;
    color: var(--color-gray-600);
    font-size: 0.9rem;
  }

  .cancel-notice {
    color: var(--color-error) !important;
  }

  .btn-manage {
    margin-top: 1rem;
    padding: 0.75rem 1.5rem;
    background: var(--color-gray-100);
    border: 1px solid var(--color-gray-200);
    border-radius: var(--radius-md);
    color: var(--color-gray-900);
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
  }

  .btn-manage:hover:not(:disabled) {
    background: var(--color-gray-200);
  }

  .btn-manage:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  /* Plans Section */
  .plans-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  .billing-toggle {
    display: flex;
    background: var(--color-gray-100);
    border-radius: var(--radius-md);
    padding: 4px;
  }

  .billing-toggle button {
    padding: 0.5rem 1rem;
    border: none;
    background: transparent;
    border-radius: var(--radius-sm);
    cursor: pointer;
    font-weight: 500;
    color: var(--color-gray-600);
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .billing-toggle button.active {
    background: white;
    color: var(--color-gray-900);
    box-shadow: var(--shadow-sm);
  }

  .save-badge {
    background: var(--color-info-bg);
    color: #1d4ed8;
    padding: 0.125rem 0.5rem;
    border-radius: 10px;
    font-size: 0.7rem;
    font-weight: 600;
  }

  .plans-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1.5rem;
  }

  .plan-card {
    background: white;
    border-radius: var(--radius-lg);
    padding: 2rem;
    box-shadow: var(--shadow-sm);
    position: relative;
    display: flex;
    flex-direction: column;
    transition: transform 0.2s, box-shadow 0.2s;
  }

  .plan-card:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }

  .plan-card.highlighted {
    border: 2px solid var(--color-primary);
  }

  .plan-card.current {
    border: 2px solid var(--color-success);
  }

  .popular-badge {
    position: absolute;
    top: -12px;
    left: 50%;
    transform: translateX(-50%);
    background: var(--gradient-primary);
    color: white;
    padding: 0.25rem 1rem;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
  }

  .current-badge {
    position: absolute;
    top: -12px;
    right: 1rem;
    background: var(--color-success);
    color: white;
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-size: 0.7rem;
    font-weight: 600;
  }

  .plan-card h3 {
    margin: 0 0 0.5rem;
    font-size: 1.5rem;
    color: var(--color-gray-900);
  }

  .plan-description {
    color: var(--color-gray-600);
    font-size: 0.9rem;
    line-height: 1.5;
    margin: 0 0 1.5rem;
  }

  .plan-price {
    margin-bottom: 0.5rem;
  }

  .currency {
    font-size: 1.25rem;
    color: var(--color-gray-900);
    vertical-align: top;
  }

  .amount {
    font-size: 3rem;
    font-weight: 700;
    color: var(--color-gray-900);
    line-height: 1;
  }

  .period {
    color: var(--color-gray-600);
    font-size: 1rem;
  }

  .annual-savings {
    color: var(--color-success);
    font-size: 0.85rem;
    font-weight: 500;
    margin: 0 0 1rem;
  }

  .features-list {
    list-style: none;
    padding: 0;
    margin: 0 0 2rem;
    flex-grow: 1;
  }

  .features-list li {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    padding: 0.5rem 0;
    color: var(--color-gray-600);
    font-size: 0.9rem;
  }

  .features-list svg {
    width: 18px;
    height: 18px;
    color: var(--color-success);
    flex-shrink: 0;
    margin-top: 2px;
  }

  .btn-subscribe,
  .btn-current {
    width: 100%;
    padding: 0.875rem 1.5rem;
    border: none;
    border-radius: var(--radius-md);
    font-weight: 600;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
  }

  .btn-subscribe {
    background: var(--color-primary);
    color: white;
  }

  .btn-subscribe:hover:not(:disabled) {
    background: var(--color-primary-hover);
    transform: translateY(-1px);
  }

  .btn-subscribe.btn-highlighted {
    background: var(--gradient-primary);
  }

  .btn-subscribe:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  .btn-current {
    background: var(--color-gray-100);
    color: var(--color-gray-600);
    cursor: default;
  }

  /* Trial user button styles */
  .btn-current-trial {
    width: 100%;
    padding: 0.625rem 1rem;
    border: 2px solid var(--color-info);
    border-radius: var(--radius-md);
    font-weight: 600;
    font-size: 0.9rem;
    background: var(--color-info-bg);
    color: var(--color-info);
    cursor: default;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
  }

  .btn-switch-tier {
    width: 100%;
    padding: 0.625rem 1rem;
    border: 2px solid var(--color-primary);
    border-radius: var(--radius-md);
    font-weight: 600;
    font-size: 0.9rem;
    background: white;
    color: var(--color-primary);
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
  }

  .btn-switch-tier:hover:not(:disabled) {
    background: var(--color-primary);
    color: white;
  }

  .btn-switch-tier:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .btn-add-payment {
    width: 100%;
    padding: 0.625rem 1rem;
    border: none;
    border-radius: var(--radius-md);
    font-weight: 500;
    font-size: 0.85rem;
    background: var(--color-gray-100);
    color: var(--color-gray-700);
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
  }

  .btn-add-payment:hover:not(:disabled) {
    background: var(--color-gray-200);
  }

  .btn-add-payment.btn-highlighted {
    background: var(--color-primary);
    color: white;
  }

  .btn-add-payment.btn-highlighted:hover:not(:disabled) {
    background: var(--color-primary-hover);
  }

  .btn-add-payment:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .btn-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  .contact-admin {
    text-align: center;
    color: var(--color-gray-600);
    font-size: 0.85rem;
    padding: 0.875rem;
    background: #f5f7fa;
    border-radius: var(--radius-md);
    margin: 0;
  }

  /* Help Section */
  .help-section {
    background: #f8fafc;
    border-radius: var(--radius-lg);
    padding: 2rem;
    text-align: center;
  }

  .help-section h3 {
    margin: 0 0 0.5rem;
    color: var(--color-gray-900);
  }

  .help-section p {
    margin: 0 0 1rem;
    color: var(--color-gray-600);
  }

  .help-link {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: var(--color-primary);
    text-decoration: none;
    font-weight: 500;
  }

  .help-link:hover {
    text-decoration: underline;
  }

  .help-link svg {
    width: 18px;
    height: 18px;
  }

  /* Success Banner - uses shared base styles */
  .success-banner {
    background: var(--color-success-bg);
    border: 1px solid #a7f3d0;
    color: var(--color-success);
    padding: 1rem 1.5rem;
    border-radius: var(--radius-md);
    margin-bottom: 2rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .success-banner svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  .success-dismiss {
    background: none;
    border: none;
    color: var(--color-success);
    cursor: pointer;
    font-weight: 500;
    margin-left: auto;
  }

  /* Subscription Card Warning State */
  .subscription-card.warning {
    border: 2px solid var(--color-warning);
  }

  .plan-badge.badge-warning {
    background: var(--color-warning-bg);
    color: var(--color-warning);
  }

  .status-dot.dot-warning {
    background: var(--color-warning);
  }

  .price-tag {
    margin-left: auto;
    font-size: 1rem;
    color: var(--color-gray-600);
    font-weight: 500;
  }

  .payment-warning {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    background: var(--color-warning-bg);
    border: 1px solid #fcd34d;
    border-radius: var(--radius-md);
    padding: 0.75rem 1rem;
    margin: 1rem 0;
    color: var(--color-warning);
    font-size: 0.85rem;
  }

  .payment-warning svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  .btn-update-card-inline {
    margin-left: auto;
    padding: 0.5rem 1rem;
    background: var(--color-warning);
    color: white;
    border: none;
    border-radius: var(--radius-sm);
    font-weight: 500;
    font-size: 0.85rem;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.2s;
  }

  .btn-update-card-inline:hover:not(:disabled) {
    background: #d97706;
  }

  .btn-update-card-inline:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  /* Billing Section */
  .billing-section {
    margin-bottom: 2rem;
  }

  .billing-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.5rem;
  }

  .billing-card {
    background: white;
    border-radius: var(--radius-lg);
    padding: 1.5rem;
    box-shadow: var(--shadow-sm);
  }

  .billing-card-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1rem;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid var(--color-gray-200);
  }

  .billing-card-header svg {
    width: 20px;
    height: 20px;
    color: var(--color-primary);
  }

  .billing-card-header h4 {
    margin: 0;
    font-size: 0.95rem;
    color: var(--color-gray-900);
  }

  .payment-method-info {
    margin-bottom: 1rem;
  }

  .card-details {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.25rem;
  }

  .card-brand {
    font-weight: 600;
    color: var(--color-gray-900);
  }

  .card-number {
    font-family: 'SF Mono', 'Monaco', 'Consolas', monospace;
    color: var(--color-gray-900);
    font-size: 1.1rem;
  }

  .card-expiry {
    margin: 0;
    font-size: 0.85rem;
    color: var(--color-gray-600);
  }

  .no-payment-method {
    color: var(--color-gray-400);
    font-size: 0.9rem;
    margin: 0 0 1rem;
  }

  .btn-update-card {
    width: 100%;
    padding: 0.625rem 1rem;
    background: var(--color-gray-100);
    border: 1px solid var(--color-gray-200);
    border-radius: var(--radius-sm);
    color: var(--color-gray-900);
    font-weight: 500;
    font-size: 0.9rem;
    cursor: pointer;
    transition: all 0.2s;
  }

  .btn-update-card:hover:not(:disabled) {
    background: var(--color-gray-200);
  }

  .btn-update-card:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .next-invoice-info {
    text-align: left;
  }

  .invoice-amount {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--color-gray-900);
    margin: 0 0 0.25rem;
  }

  .invoice-date {
    font-size: 0.9rem;
    color: var(--color-gray-600);
    margin: 0;
  }

  .no-invoice {
    color: var(--color-gray-400);
    font-size: 0.9rem;
    margin: 0;
  }

  /* Invoices Section */
  .invoices-section {
    margin-bottom: 2rem;
  }

  .invoices-list {
    background: white;
    border-radius: var(--radius-lg);
    overflow: hidden;
    box-shadow: var(--shadow-sm);
  }

  .invoice-row {
    display: grid;
    grid-template-columns: 1fr 1fr 100px 120px;
    gap: 1rem;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid var(--color-gray-200);
    align-items: center;
  }

  .invoice-row:last-child {
    border-bottom: none;
  }

  .invoice-date-col {
    color: var(--color-gray-900);
    font-size: 0.9rem;
  }

  .invoice-amount-col {
    font-weight: 600;
    color: var(--color-gray-900);
  }

  .invoice-status-col {
    text-align: center;
  }

  /* Status badges use shared base with semantic colors */
  .status-badge {
    display: inline-block;
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: capitalize;
  }

  .status-success {
    background: var(--color-success-bg);
    color: var(--color-success);
  }

  .status-warning {
    background: var(--color-warning-bg);
    color: var(--color-warning);
  }

  .status-error {
    background: var(--color-error-bg);
    color: var(--color-error);
  }

  .status-neutral {
    background: var(--color-gray-100);
    color: var(--color-gray-600);
  }

  .invoice-actions-col {
    text-align: right;
  }

  .invoice-link {
    color: var(--color-primary);
    text-decoration: none;
    font-size: 0.85rem;
    font-weight: 500;
  }

  .invoice-link:hover {
    text-decoration: underline;
  }

  .btn-view-all {
    display: block;
    width: 100%;
    margin-top: 1rem;
    padding: 0.75rem;
    background: transparent;
    border: 1px solid var(--color-gray-200);
    border-radius: var(--radius-md);
    color: var(--color-primary);
    font-weight: 500;
    font-size: 0.9rem;
    cursor: pointer;
    transition: all 0.2s;
  }

  .btn-view-all:hover {
    background: #f5f7fa;
    border-color: var(--color-primary);
  }

  /* Responsive */
  @media (max-width: 768px) {
    .plans-header {
      flex-direction: column;
      align-items: flex-start;
    }

    .billing-toggle {
      width: 100%;
    }

    .billing-toggle button {
      flex: 1;
      justify-content: center;
    }

    .plans-grid {
      grid-template-columns: 1fr;
    }

    .billing-grid {
      grid-template-columns: 1fr;
    }

    .invoice-row {
      grid-template-columns: 1fr 1fr;
      gap: 0.5rem;
    }

    .invoice-status-col {
      text-align: left;
    }

    .invoice-actions-col {
      text-align: left;
    }

    .payment-warning {
      flex-wrap: wrap;
    }

    .btn-update-card-inline {
      margin-left: 0;
      margin-top: 0.5rem;
      width: 100%;
    }

    .subscription-header {
      flex-wrap: wrap;
    }

    .price-tag {
      width: 100%;
      margin-left: 0;
      margin-top: 0.5rem;
    }
  }
</style>
